import { TemplateResult } from 'lit';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import {
  RowDeletionInterface,
  RowEditingInterface,
  RowReorderingInterface,
  RowSelectionInterface,
  TableFooterProtectedInterface,
  RowReorderingProtectedInterface,
  RowEditingProtectedInterface,
  RowDeletionProtectedInterface,
  RowSelectionProtectedInterface,
} from '../decorators';
import { FormValidator } from '../../gzForm/formField/types/form-validator';

// this type should be used when creating a GzDataTableWebComponent instance,
// since it includes the public methods and attributes from all the decorators
export type DecoratedDataTableWebComponent<TDataTableItem = unknown> = GzDataTableWebComponent<TDataTableItem> &
  RowDeletionInterface &
  RowEditingInterface &
  RowReorderingInterface &
  RowSelectionInterface;

// this type should only be used by the decorators of GzDataTableWebComponent, since it also includes the protected methods
export type ProtectedDataTableWebComponent<TDataTableItem = unknown> = DecoratedDataTableWebComponent<TDataTableItem> &
  TableFooterProtectedInterface &
  RowReorderingProtectedInterface &
  RowEditingProtectedInterface &
  RowDeletionProtectedInterface &
  RowSelectionProtectedInterface;

export enum IColumnType {
  DISPLAY = 'display', // by setting this, no input field will be displayed on row edit
  TEXT = 'text',
  SINGLE_SELECT = 'single-select',
  MULTI_SELECT = 'multi-select',
  NUMBER = 'number',
}

export interface IColumnOption {
  label: string;
  value: string | number;
  selected?: boolean;
  disabled?: boolean;
}

export enum DefaultColumnKeys {
  REORDER = 'default-column-id-reorder',
  MULTI_SELECT = 'default-column-id-select',
  PRIORITY = 'default-column-id-priority',
  ACTIONS = 'default-column-id-actions',
}

export interface IDataTableColumn {
  key: string;
  header: string;
  columnType: IColumnType; // determines the type of the input field displayed on row edit
  options?: IColumnOption[]; // if column type is single/multi select; provides options to the dropdown
  maxValue?: number; // if column type is number; provides the upper limit for the number input on row edit
  minValue?: number; // if column type is number; provides the lower limit for the number input on row edit
  numberSuffix?: string; // if column type is number, this provides a suffix to display after the number value (e.g. %, px)
  width?: string;
  editable?: boolean;
  copyable?: boolean;
  frozen?: boolean;
  validators?: FormValidator[]; // for row editing
  sanitizer?: (value: unknown) => unknown; // for row editing
  renderer?: (
    content: unknown,
    column: IDataTableColumn,
    rowIndex: number,
    editMode?: boolean,
    item?: any
  ) => TemplateResult;
  headerRenderer?: (column: IDataTableColumn) => TemplateResult;
  footerRenderer?: (column: IDataTableColumn) => TemplateResult;
}

export interface DataTableLoaderLayout {
  rows: number;
  columns: number;
}

export interface DataTableStrings {
  copyConfirmationTooltip: string;
  emptyStateFooterButton: string;
  deleteRowTooltip: string;
  resetPriorityButton: string;
  editRowTooltip: string;
  cancelEditTooltip: string;
  validateEditTooltip: string;
  disabledEditTooltip: string;
  requiredFieldMessage: string;
  extraValidationToastMessage: string;
  textInputPlaceholder: string;
  dropdownApplyButtonString: string;
  dropdownAllItemsLabel: string;
}

export enum DataTableEvents {
  ROWS_REORDERED = 'rows-reordered',
  ROWS_RESET_ORDER = 'rows-reset-order',
  SELECTION_CHANGED = 'selection-changed',
  ROW_DELETED = 'row-deleted',
  ROW_EDITED = 'row-edited',
  ROW_EDITING_STARTED = 'row-editing-started',
  ROW_EDITING_CANCELLED = 'row-editing-cancelled',
  EMPTY_STATE_FOOTER_BUTTON_CLICKED = 'empty-state-footer-button-clicked',
}

export enum DataTableSelectionMode {
  SINGLE = 'single',
  MULTI = 'multi',
}

// Regarding the width of all the columns which do not have a fixed width set via the IDataTableColumn interface
export enum DataTableDisplayType {
  // Width will be distributed equally (flex)
  FLEX = 'flex',
  // Width will be set to match the width of the content (but maximum 400px);
  // This option will make the table flicker on every rerender, because the width is computed only after rendering,
  // for performance reasons: https://cdn.vaadin.com/vaadin-grid/5.8.4/#/elements/vaadin-grid-selection-column#property-autoWidth
  AUTO_WIDTH = 'auto-width',
}
